import { Input, Modal, Switch } from "@arco-design/web-react";
import { useEffect, useState } from "react";

import type {
  DicMajorAliasItem,
  DicMajorAliasMutation,
  DicMajorItem,
  DicMajorMutation,
} from "../types";
import "./DicMajorPage.css";

type ModalState =
  | { type: "major-create" }
  | { type: "major-edit"; major: DicMajorItem }
  | { type: "alias-create"; major: DicMajorItem }
  | { type: "alias-edit"; major: DicMajorItem; alias: DicMajorAliasItem };

type DicMajorMutationModalProps = {
  state: ModalState | null;
  onClose: () => void;
  errorMessage: string;
  onCreateMajor: (payload: DicMajorMutation) => Promise<void>;
  onUpdateMajor: (major: DicMajorItem, payload: DicMajorMutation) => Promise<void>;
  onCreateAlias: (major: DicMajorItem, payload: DicMajorAliasMutation) => Promise<void>;
  onUpdateAlias: (
    major: DicMajorItem,
    alias: DicMajorAliasItem,
    payload: DicMajorAliasMutation,
  ) => Promise<void>;
  onClearError: () => void;
};

const emptyMajorPayload: DicMajorMutation = {
  majorName: "",
  majorCategory: "",
  spellCode: "",
  sortNo: 0,
  isDisabled: false,
  remark: "",
};

const emptyAliasPayload: DicMajorAliasMutation = {
  aliasName: "",
  isDisabled: false,
  remark: "",
};

/** 标准专业与别名编辑弹窗。 */
export function DicMajorMutationModal({
  state,
  onClose,
  errorMessage,
  onCreateMajor,
  onUpdateMajor,
  onCreateAlias,
  onUpdateAlias,
  onClearError,
}: DicMajorMutationModalProps) {
  const [majorPayload, setMajorPayload] = useState<DicMajorMutation>(emptyMajorPayload);
  const [aliasPayload, setAliasPayload] = useState<DicMajorAliasMutation>(emptyAliasPayload);
  const [localErrorMessage, setLocalErrorMessage] = useState("");
  const displayedErrorMessage = localErrorMessage || errorMessage;

  useEffect(() => {
    if (!state) {
      return;
    }
    onClearError();
    setLocalErrorMessage("");
    if (state.type === "major-edit") {
      setMajorPayload({
        majorName: state.major.majorName,
        majorCategory: state.major.majorCategory,
        spellCode: state.major.spellCode,
        sortNo: state.major.sortNo,
        isDisabled: state.major.isDisabled,
        remark: state.major.remark,
      });
      return;
    }
    if (state.type === "alias-edit") {
      setAliasPayload({
        aliasName: state.alias.aliasName,
        isDisabled: state.alias.isDisabled,
        remark: state.alias.remark,
      });
      return;
    }
    setMajorPayload(emptyMajorPayload);
    setAliasPayload(emptyAliasPayload);
  }, [state]);

  const isAlias = state?.type === "alias-create" || state?.type === "alias-edit";
  const title = isAlias
    ? state?.type === "alias-edit"
      ? "编辑专业别名"
      : "新增专业别名"
    : state?.type === "major-edit"
      ? "编辑标准专业"
      : "新增标准专业";

  /** 提交当前弹窗表单。 */
  async function handleOk() {
    if (!state) {
      return;
    }
    if (!isAlias) {
      if (!majorPayload.majorName.trim()) {
        setLocalErrorMessage("请填写标准专业名称。");
        return;
      }
      if (!majorPayload.majorCategory.trim()) {
        setLocalErrorMessage("请填写专业大类。");
        return;
      }
    } else if (!aliasPayload.aliasName.trim()) {
      setLocalErrorMessage("请填写别名名称。");
      return;
    }
    setLocalErrorMessage("");
    onClearError();
    try {
      if (state.type === "major-create") {
        await onCreateMajor(majorPayload);
      } else if (state.type === "major-edit") {
        await onUpdateMajor(state.major, majorPayload);
      } else if (state.type === "alias-create") {
        await onCreateAlias(state.major, aliasPayload);
      } else {
        await onUpdateAlias(state.major, state.alias, aliasPayload);
      }
      onClose();
    } catch {
      // runtime 已写入错误提示，弹窗不关闭。
    }
  }

  return (
    <Modal
      title={title}
      visible={Boolean(state)}
      onCancel={() => {
        setLocalErrorMessage("");
        onClearError();
        onClose();
      }}
      onOk={handleOk}
      okText="保存"
      cancelText="取消"
      unmountOnExit
    >
      {displayedErrorMessage ? (
        <div className="dic-major-error" role="alert">
          {displayedErrorMessage}
        </div>
      ) : null}
      {isAlias ? (
        <div className="dic-major-modal-form">
          <label className="dic-major-field">
            <span>别名名称</span>
            <Input
              value={aliasPayload.aliasName}
              onChange={(aliasName) => setAliasPayload({ ...aliasPayload, aliasName })}
            />
          </label>
          <label className="dic-major-switch-field">
            <span>停用</span>
            <Switch
              checked={aliasPayload.isDisabled}
              onChange={(isDisabled) => setAliasPayload({ ...aliasPayload, isDisabled })}
            />
          </label>
          <label className="dic-major-field">
            <span>备注</span>
            <Input.TextArea
              value={aliasPayload.remark}
              onChange={(remark) => setAliasPayload({ ...aliasPayload, remark })}
            />
          </label>
        </div>
      ) : (
        <div className="dic-major-modal-form">
          <label className="dic-major-field">
            <span>标准专业名称</span>
            <Input
              aria-label="标准专业名称"
              value={majorPayload.majorName}
              onChange={(majorName) => setMajorPayload({ ...majorPayload, majorName })}
            />
          </label>
          <label className="dic-major-field">
            <span>专业大类</span>
            <Input
              aria-label="专业大类"
              value={majorPayload.majorCategory}
              onChange={(majorCategory) => setMajorPayload({ ...majorPayload, majorCategory })}
            />
          </label>
          <label className="dic-major-field">
            <span>拼音码</span>
            <Input
              aria-label="拼音码"
              placeholder="可留空，保存时自动生成"
              value={majorPayload.spellCode}
              onChange={(spellCode) => setMajorPayload({ ...majorPayload, spellCode })}
            />
          </label>
          <label className="dic-major-field">
            <span>排序号</span>
            <input
              className="dic-major-number-input"
              type="number"
              value={majorPayload.sortNo}
              onChange={(event) =>
                setMajorPayload({ ...majorPayload, sortNo: Number(event.target.value) })
              }
            />
          </label>
          <label className="dic-major-switch-field">
            <span>停用</span>
            <Switch
              checked={majorPayload.isDisabled}
              onChange={(isDisabled) => setMajorPayload({ ...majorPayload, isDisabled })}
            />
          </label>
          <label className="dic-major-field">
            <span>备注</span>
            <Input.TextArea
              value={majorPayload.remark}
              onChange={(remark) => setMajorPayload({ ...majorPayload, remark })}
            />
          </label>
        </div>
      )}
    </Modal>
  );
}
